import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton,
  List, ListItemButton, ListItemText, Snackbar, Tooltip, Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { ArrowBack, Download, Visibility, ChevronLeft, ChevronRight, DeleteOutline, Send, EventAvailable, CalendarViewMonth, Description, Edit, RequestQuote, Notes, Gavel, History, SwapHoriz } from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Breadcrumb } from '../../../components/layout/Breadcrumb';
import { ConfirmDialog, CONFIRM_INITIAL, type ConfirmDialogState } from '../../../components/shared/ConfirmDialog';
import { ROUTES } from '../../../constants/routes';
import {
  getQuote, updateQuote, duplicateQuote, downloadQuotePdf, deleteQuote,
} from '../../../services/quoteService';
import { generateProposal, listProposalDocuments } from '../../../services/quoteProposal';
import type { ProposalDocument } from '../../../types/schedule';
import { fetchCommitStatus, type CommitStatusSummary } from '../../../services/scheduleCommit';
import {
  useSourceScheduleImpact,
  isSyncBlocked,
  buildSyncSnackbar,
  buildSyncReleaseDialog,
  SYNC_FAILED_MESSAGE,
} from '../../../hooks/useSourceScheduleImpact';
import { ScheduleLinkBanner } from '../../../components/schedule/ScheduleLinkBanner';
import { usePermissions } from '../../../hooks/usePermissions';
import { roleAtLeast } from '../../../constants/roles';
import { isQuoteScheduleable, type QuoteDetail, type QuoteStatus } from '../../../types/quote';
import type { ScheduleReturnTo } from '../../../types/schedule';
import { QuoteHeaderSection } from './QuoteHeaderSection';
import { QuotePricingSection } from './QuotePricingSection';
import { QuoteScopeSection } from './QuoteScopeSection';
import { QuoteTermsSection } from './QuoteTermsSection';
import { QuoteSummary } from './QuoteSummary';
import { QuotePdfPreview } from './QuotePdfPreview';
import { QuoteApprovalSection } from './QuoteApprovalSection';
import { QuoteSendDialog } from './QuoteSendDialog';
import { QuoteActivityLog } from './QuoteActivityLog';
import { QuotePreviewScheduleModal } from './QuotePreviewScheduleModal';
import { OverrideStatusDialog } from './OverrideStatusDialog';
import type { StatusChangeResponse } from '../../../services/quoteService';

type Section = 'header' | 'pricing' | 'scope' | 'terms' | 'activity';

const SECTIONS: { key: Section; label: string; Icon: SvgIconComponent }[] = [
  { key: 'header', label: 'Header', Icon: Description },
  { key: 'pricing', label: 'Pricing', Icon: RequestQuote },
  { key: 'scope', label: 'Scope & Notes', Icon: Notes },
  { key: 'terms', label: 'Terms', Icon: Gavel },
  { key: 'activity', label: 'Activity', Icon: History },
];

const STATUS_COLORS: Record<QuoteStatus, string> = {
  Draft: '#9e9e9e', 'Internal Review': '#7b1fa2', 'Approved Internal': '#1565c0',
  Rejected: '#d32f2f', 'Changes Requested': '#e65100',
  Sent: '#1976d2', Viewed: '#0288d1', Accepted: '#2e7d32',
  Declined: '#c62828', Expired: '#e65100', Withdrawn: '#616161', Converted: '#00695c',
};

/** Partial shape of a quote update response that includes schedule release info. */
interface QuoteUpdateResponse extends Partial<QuoteDetail> {
  scheduleRelease?: { tasksReleased: number; bookingsReleased: number };
  /** W2-5 (P-19b): draft plans auto-deleted on a Declined/Withdrawn transition. */
  scenariosDeleted?: number;
}

export const QuoteEditorPage = () => {
  const { quoteKey } = useParams<{ quoteKey: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { role } = usePermissions();
  const canDelete = role === 'technician' || role === 'admin' || role === 'superadmin';
  /** Manual override (2026-07-24): the admin-only status escape hatch. */
  const canOverrideStatus = roleAtLeast(role, 'admin');

  const [quote, setQuote] = useState<QuoteDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<Section>('header');
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [sideOpen, setSideOpen] = useState(true);
  const [confirmDlg, setConfirmDlg] = useState<ConfirmDialogState>(CONFIRM_INITIAL);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  /** Manual override (2026-07-24): admin-only Override Status dialog. */
  const [overrideStatusOpen, setOverrideStatusOpen] = useState(false);

  /**
   * Immediate release message shown after a status transition to Declined/Withdrawn.
   * Set from the API response; persists until the user navigates away.
   * Passed to QuoteApprovalSection as `freshReleaseCount`.
   */
  const [freshReleaseCount, setFreshReleaseCount] = useState<number | null>(null);

  /** Phase 5 Item 5 — Preview Schedule modal open state. */
  const [previewScheduleOpen, setPreviewScheduleOpen] = useState(false);

  /** Result snackbar — errors from Commit Schedule and other async actions. */
  const [scheduleSnack, setScheduleSnack] = useState<{ severity: 'success' | 'error' | 'warning'; message: string } | null>(null);

  /** Commit status summary — drives the Commit Schedule button label/enabled state. */
  const [commitStatus, setCommitStatus] = useState<CommitStatusSummary | null>(null);

  /**
   * FEAT-2026-0012 — quote ↔ scenario-planning sync. Drives the persistent
   * ScheduleLinkBanner and the warn-before-delete cascade.
   */
  const {
    impact: scheduleImpact,
    loading: impactLoading,
    syncing: impactSyncing,
    sync: syncSchedule,
    syncConfirmed: syncScheduleConfirmed,
    buildDeleteDialog: buildScheduleDeleteDialog,
  } = useSourceScheduleImpact('Quote', quoteKey);

  /** Phase 5 Item 7 — Generate Proposal in-flight. */
  const [generatingProposal, setGeneratingProposal] = useState(false);

  /** Phase 5 Item 8 — ProposalDocument rows for this quote (latest first). */
  const [proposalDocs, setProposalDocs] = useState<ProposalDocument[]>([]);

  const refreshProposalDocs = useCallback(async () => {
    if (!quoteKey) return;
    try {
      const docs = await listProposalDocuments(quoteKey);
      setProposalDocs(docs);
    } catch {
      // non-fatal — "Last generated" just won't show
    }
  }, [quoteKey]);

  const refreshCommitStatus = useCallback(async () => {
    if (!quoteKey) return;
    try {
      const s = await fetchCommitStatus({ source: 'Quote', sourceKey: quoteKey });
      setCommitStatus(s);
    } catch {
      // non-fatal — leave previous state
    }
  }, [quoteKey]);

  // Fetch commit status whenever the quote loads or quoteKey changes
  useEffect(() => { refreshCommitStatus(); }, [refreshCommitStatus]);

  const fetchQuote = useCallback(async () => {
    if (!quoteKey) return;
    setLoading(true);
    try { setQuote(await getQuote(quoteKey)); }
    catch { setQuote(null); }
    finally { setLoading(false); }
  }, [quoteKey]);

  const softRefresh = useCallback(async () => {
    if (!quoteKey) return;
    try { setQuote(await getQuote(quoteKey)); } catch { /* */ }
  }, [quoteKey]);

  useEffect(() => { fetchQuote(); }, [fetchQuote]);
  useEffect(() => { refreshProposalDocs(); }, [refreshProposalDocs]);

  const handleUpdateQuote = useCallback(async (
    data: Record<string, unknown>,
    opts?: { releaseSchedule?: boolean },
  ) => {
    if (!quoteKey || !quote) return;
    try {
      const updated = await updateQuote(quoteKey, data, opts) as QuoteUpdateResponse;

      // Surface release banner when transitioning to Declined or Withdrawn
      if (updated.scheduleRelease) {
        setFreshReleaseCount(updated.scheduleRelease.bookingsReleased);
      }
      // W2-5 (P-19b): the transition also auto-deleted the dead draft plan(s).
      if (updated.scenariosDeleted) {
        setScheduleSnack({
          severity: 'success',
          message: `${updated.scenariosDeleted} draft plan(s) deleted from Scenario Planning.`,
        });
      }

      // Merge server response into existing quote — server doesn't return lineItems
      setQuote((prev) => prev ? { ...prev, ...updated, lineItems: prev.lineItems } : prev);
      // Soft refresh to get recalculated totals with line items intact
      softRefresh();
    } catch (err) {
      // P2-4: a Declined/Withdrawn transition that would release COMMITTED
      // bookings is refused with 409 SOURCE_RELEASES_COMMITTED — confirm
      // ("Release & continue") and retry with releaseSchedule=true. Same
      // ceremony as the delete door; nothing was written on refusal.
      const resp = (err as {
        response?: {
          status?: number;
          data?: { code?: string; error?: string; committedBookings?: number; taskNames?: string[] };
        };
      }).response;
      if (resp?.status === 409 && resp.data?.code === 'SOURCE_RELEASES_COMMITTED') {
        const committed = resp.data.committedBookings ?? 0;
        const tasks = resp.data.taskNames ?? [];
        const statusLabel = String(data.Status ?? 'this status');
        setConfirmDlg({
          open: true,
          title: `Mark quote ${statusLabel} — live schedule attached`,
          message:
            `Marking this quote ${statusLabel} will release ${committed} committed booking(s) from the live schedule` +
            (tasks.length > 0 ? `:\n\n${tasks.map((n) => `• ${n}`).join('\n')}\n\n` : '. ') +
            'This cannot be undone. Choose "Release & continue" to proceed.',
          confirmLabel: 'Keep schedule',
          confirmColor: 'primary',
          // Confirm button is the SAFE path; the destructive release lives on
          // the explicit secondary button (delete-door ceremony).
          onConfirm: () => { /* safe no-op */ },
          secondaryLabel: 'Release & continue',
          secondaryColor: 'error',
          onSecondary: () => { void handleUpdateQuote(data, { releaseSchedule: true }); },
        });
      }
    }
  }, [quoteKey, quote, softRefresh]);

  /**
   * Manual override (2026-07-24): the OverrideStatusDialog handled the API
   * call (including the committed-work 409 confirm) — surface any schedule
   * release / scenario cleanup exactly like handleUpdateQuote does, then
   * refetch.
   */
  const handleStatusOverridden = useCallback((result: StatusChangeResponse) => {
    setOverrideStatusOpen(false);
    if (result.scheduleRelease) {
      setFreshReleaseCount(result.scheduleRelease.bookingsReleased);
    }
    if (result.scenariosDeleted) {
      setScheduleSnack({
        severity: 'success',
        message: `${result.scenariosDeleted} draft plan(s) deleted from Scenario Planning.`,
      });
    }
    void softRefresh();
  }, [softRefresh]);

  const handleDuplicate = useCallback(async () => {
    if (!quoteKey) return;
    try {
      const dup = await duplicateQuote(quoteKey);
      navigate(ROUTES.QUOTE_EDITOR.replace(':quoteKey', dup.QuoteKey));
    } catch { /* */ }
  }, [quoteKey, navigate]);

  const handleDownloadPdf = useCallback(async () => {
    if (!quote) return;
    try { await downloadQuotePdf(quote.QuoteKey, quote.QuoteNumber); } catch { /* */ }
  }, [quote]);

  const handleDelete = useCallback(async (releaseSchedule = false) => {
    if (!quoteKey) return;
    try {
      await deleteQuote(quoteKey, releaseSchedule ? { releaseSchedule: true } : undefined);
      navigate(ROUTES.QUOTES);
    } catch (err) {
      // P2-8/P2-9: prefer the server's human message (e.g. the committed-work
      // 409 "Confirm the release to delete") over axios's raw status string.
      const apiMsg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
        : undefined;
      setScheduleSnack({
        severity: 'error',
        message: apiMsg ?? (err instanceof Error ? err.message : 'Failed to delete quote.'),
      });
    }
  }, [quoteKey, navigate]);

  /**
   * FEAT-2026-0012 — Delete entry point. If a scenario is attached, show the
   * cascade (or strong finalized-release) warning built by the hook; otherwise
   * keep the existing plain "permanently delete" confirm.
   */
  const handleDeleteClick = useCallback(() => {
    if (!quote) return;
    const dlg = buildScheduleDeleteDialog({
      sourceLabel: quote.QuoteNumber || 'this quote',
      onDelete: (releaseSchedule) => handleDelete(releaseSchedule),
    });
    setConfirmDlg(dlg ?? {
      open: true,
      title: 'Delete Quote',
      message: `Permanently delete ${quote.QuoteNumber}? This cannot be undone.`,
      confirmLabel: 'Delete',
      confirmColor: 'error',
      onConfirm: () => { void handleDelete(false); },
    });
  }, [quote, buildScheduleDeleteDialog, handleDelete]);

  /**
   * FEAT-2026-0012 — Sync now. Re-syncs the attached scenario's tasks with the
   * quote's current line items, then reports the change counts.
   *
   * W2-4 (P-20b): when the sync would release COMMITTED bookings the backend
   * refuses (409) and sync() resolves blocked — raise the shared ConfirmDialog
   * ("Release & sync" / Cancel) and only retry with confirmRelease on confirm.
   * Cancel is a no-op. The confirmed path keeps the normal result snackbar.
   */
  const handleSyncSchedule = useCallback(async () => {
    try {
      const result = await syncSchedule();
      if (!result) return;
      if (isSyncBlocked(result)) {
        setConfirmDlg(buildSyncReleaseDialog(result, async () => {
          try {
            const confirmed = await syncScheduleConfirmed();
            if (confirmed) setScheduleSnack(buildSyncSnackbar(confirmed));
          } catch {
            // P2-11: the pipeline is transactional per step + idempotent —
            // re-running Sync converges after a mid-pipeline failure.
            setScheduleSnack({ severity: 'error', message: SYNC_FAILED_MESSAGE });
          }
        }));
        return;
      }
      setScheduleSnack(buildSyncSnackbar(result));
    } catch {
      setScheduleSnack({ severity: 'error', message: SYNC_FAILED_MESSAGE });
    }
  }, [syncSchedule, syncScheduleConfirmed]);

  /**
   * Add to Schedule — FEAT-2026-0007 6D.3: navigate to Scenario Planning pre-filtered
   * to this quote. Task generation happens scenario-scoped on that page (the picker
   * creates a Draft scenario first, then generates under it) — doing it here would
   * write tasks under BASELINE and pollute the live schedule.
   */
  const handleAddToSchedule = useCallback(() => {
    if (!quoteKey) return;
    // Back-nav fix (2026-07-11): carry the origin as navigation state so
    // Scenario Planning can render an explicit "Back to Quote {number}" button.
    const scheduleReturnTo: ScheduleReturnTo = {
      path: `${location.pathname}${location.search}`,
      label: quote?.QuoteNumber ? `Quote ${quote.QuoteNumber}` : 'Quote',
    };
    navigate(`${ROUTES.SCHEDULE_SCENARIO}?source=Quote&sourceKey=${encodeURIComponent(quoteKey)}`, {
      state: { scheduleReturnTo },
    });
  }, [quoteKey, navigate, location.pathname, location.search, quote?.QuoteNumber]);

  /**
   * Edit Schedule — replaces "Add to Schedule" once a schedule already exists
   * for this quote (BUG-2026-0071). FEAT-2026-0007 6D.3: navigate to Scenario
   * Planning pre-filtered to this quote so the planner can adjust machine,
   * technician, or date selections inside a named scenario.
   */
  // Same navigation + return-to state as Add to Schedule (also wired to the
  // ScheduleLinkBanner's "View schedule" action).
  const handleEditSchedule = handleAddToSchedule;

  // Commit Schedule moved OUT of the quote page (2026-08-06, Vahid): commits
  // happen in Scenario Planning, where Finalize & Convert to Work Order is
  // the single door that commits the schedule AND converts the quote.

  /**
   * Generate Proposal — Phase 5, Items 7 + 8.
   * Calls the backend to assemble a .docx, downloads it to the browser,
   * then refreshes the ProposalDocument list so "Last generated" updates.
   */
  const handleGenerateProposal = useCallback(async () => {
    if (!quote) return;
    setGeneratingProposal(true);
    try {
      await generateProposal(quote.QuoteKey, quote.QuoteNumber);
      // Refresh the persisted document list after a successful generate.
      await refreshProposalDocs();
    } catch (err) {
      setScheduleSnack({
        severity: 'error',
        message: err instanceof Error ? err.message : 'Failed to generate proposal.',
      });
    } finally {
      setGeneratingProposal(false);
    }
  }, [quote, refreshProposalDocs]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}><CircularProgress /></Box>;
  if (!quote) return (
    <Box sx={{ pt: 4, textAlign: 'center' }}>
      <Typography color="error">Quote not found.</Typography>
      <Button onClick={() => navigate(ROUTES.QUOTES)} sx={{ mt: 2, textTransform: 'none' }}>Back to Quotes</Button>
    </Box>
  );

  const statusColor = STATUS_COLORS[quote.Status] || theme.palette.text.secondary;
  // REQ-01: internal quotes are schedule-only — they bypass approval and are
  // never sent to a client.
  const isInternal = quote.QuoteType === 'Internal';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', overflow: 'hidden', mx: -3, mt: -2, mb: -2 }}>
      {/* Top bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 0.75, borderBottom: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
        <IconButton size="small" onClick={() => navigate(ROUTES.QUOTES)}><ArrowBack fontSize="small" /></IconButton>
        <Breadcrumb items={[{ label: 'Quotes', path: ROUTES.QUOTES }, { label: quote.QuoteNumber || 'New Quote' }]} />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>Rev {quote.Revision}</Typography>
        <Chip label={quote.Status} size="small" sx={{ bgcolor: alpha(statusColor, 0.12), color: statusColor, fontWeight: 600, fontSize: '0.72rem' }} />
        {isInternal && (
          <Chip label="Internal" size="small" variant="outlined"
            sx={{ fontWeight: 600, fontSize: '0.72rem', borderColor: theme.palette.text.secondary, color: theme.palette.text.secondary }} />
        )}
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" size="small" startIcon={<Visibility />} onClick={() => setPdfPreviewOpen(true)}
          sx={{ textTransform: 'none', fontSize: '0.75rem' }}>Preview PDF</Button>
        <Button variant="outlined" size="small" startIcon={<Download />} onClick={handleDownloadPdf}
          sx={{ textTransform: 'none', fontSize: '0.75rem' }}>Download PDF</Button>
        {/*
          BUG-2026-0071 — Once a schedule already exists for this quote, swap
          "Add to Schedule" for "Edit Schedule" so users don't think they need
          to re-generate (and to make it obvious where to go to adjust
          machine / tech / date selections). hasSchedule is derived from
          commitStatus, which is already fetched on mount.
        */}
        {quote.Status !== 'Converted' && (() => {
          const tentTotal =
            (commitStatus?.tentativeBookings ?? 0) +
            (commitStatus?.tentativeAssignments ?? 0);
          const commTotal =
            (commitStatus?.committedBookings ?? 0) +
            (commitStatus?.committedAssignments ?? 0);
          const hasSchedule =
            !!commitStatus &&
            ((commitStatus.totalTasks ?? 0) > 0 || tentTotal > 0 || commTotal > 0);
          if (hasSchedule) {
            return (
              <Tooltip title="Open the Machine Schedule filtered to this quote to adjust machine, technician, or date selections.">
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Edit />}
                    onClick={handleEditSchedule}
                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                  >
                    Edit Schedule
                  </Button>
                </span>
              </Tooltip>
            );
          }
          // BUG-2026-0083 — Block "Add to Schedule" until the quote has passed
          // internal review. Draft / Internal Review / Rejected / Changes
          // Requested all map to "not yet approved internally" and the schedule
          // would churn if line items still change. Earliest allowed status =
          // "Approved Internal". Tooltip explains the gate.
          const scheduleableByStatus = isQuoteScheduleable(quote.Status, quote.QuoteType);
          const hasLineItems = !!quote.lineItems && quote.lineItems.length > 0;
          const tooltipTitle = !scheduleableByStatus
            ? `Quote must pass internal review before it can be added to the schedule (current status: ${quote.Status}).`
            : !hasLineItems
              ? 'Add at least one line item before scheduling.'
              : 'Generate schedule tasks from this quote’s line items.';
          return (
            <Tooltip title={tooltipTitle}>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EventAvailable />}
                  onClick={handleAddToSchedule}
                  disabled={!hasLineItems || !scheduleableByStatus}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Add to Schedule
                </Button>
              </span>
            </Tooltip>
          );
        })()}
        {/* Phase 5 Item 5 — Preview Schedule: read-only Gantt of this quote's tentative bookings */}
        {quote.Status !== 'Converted' && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<CalendarViewMonth />}
          onClick={() => setPreviewScheduleOpen(true)}
          sx={{ textTransform: 'none', fontSize: '0.75rem' }}
        >
          Preview Schedule
        </Button>
        )}
        {/* Phase 5 Items 7 + 8 — Generate Proposal: downloads a structured .docx and shows "Last generated" */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={generatingProposal ? <CircularProgress size={14} /> : <Description />}
            onClick={handleGenerateProposal}
            disabled={generatingProposal || !quote.lineItems || quote.lineItems.length === 0}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
          >
            Generate Proposal
          </Button>
          {proposalDocs.length > 0 && (
            <Typography
              variant="caption"
              sx={{ fontSize: '0.65rem', color: 'text.secondary', mt: 0.25, lineHeight: 1.2 }}
            >
              Last generated: {new Date(proposalDocs[0].GeneratedAt).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' })}{' '}
              by {proposalDocs[0].GeneratedBy} (v{proposalDocs[0].Version})
            </Typography>
          )}
        </Box>
        {quote.Status !== 'Converted' && commitStatus !== null && (() => {
          // Read-only schedule state — committing now lives ONLY in Scenario
          // Planning (Finalize & Convert to Work Order). This caption tells the
          // user where the door went instead of silently losing it.
          const tentTotal = (commitStatus?.tentativeBookings ?? 0) + (commitStatus?.tentativeAssignments ?? 0);
          const commTotal = (commitStatus?.committedBookings ?? 0) + (commitStatus?.committedAssignments ?? 0);
          if (tentTotal === 0 && commTotal === 0) return null;
          return (
            <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary', alignSelf: 'center' }}>
              {commTotal > 0 && tentTotal === 0
                ? `Schedule committed (${commTotal})`
                : `${tentTotal} tentative on the schedule — finalize in Scenario Planning to commit & convert`}
            </Typography>
          );
        })()}
        {!isInternal && quote.Status === 'Approved Internal' && (
          <Button variant="contained" size="small" startIcon={<Send />} onClick={() => setSendDialogOpen(true)}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}>Send to Client</Button>
        )}
        {/* Manual override (2026-07-24): admin-only escape hatch for quotes
            sent/answered outside the system when no workflow button fits.
            Blocked for Converted quotes — the WO already exists. */}
        {canOverrideStatus && quote.Status !== 'Converted' && (
          <Tooltip title="Admin: force this quote to another status (audited, reason required).">
            <Button variant="outlined" size="small" color="warning" startIcon={<SwapHoriz />}
              onClick={() => setOverrideStatusOpen(true)}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}>Override Status</Button>
          </Tooltip>
        )}
        {canDelete && (
          <Button variant="outlined" size="small" color="error" startIcon={<DeleteOutline />}
            onClick={handleDeleteClick}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}>Delete</Button>
        )}
      </Box>

      {/* Main layout */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left nav — modern, prominent, single-line items */}
        <Box sx={{
          flexShrink: 0, borderRight: `1px solid ${theme.palette.divider}`,
          transition: 'width 0.25s ease', width: sideOpen ? 232 : 56, overflow: 'hidden',
          bgcolor: alpha(theme.palette.primary.main, 0.025),
          display: 'flex', flexDirection: 'column',
        }}>
          {/* Header row: quote number + collapse toggle */}
          <Box sx={{
            display: 'flex', alignItems: 'center',
            justifyContent: sideOpen ? 'space-between' : 'center',
            pl: sideOpen ? 1.75 : 0, pr: sideOpen ? 0.5 : 0, pt: 1.25, pb: 0.5, minHeight: 36,
          }}>
            {sideOpen && (
              <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 0.6, color: 'text.secondary', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                {quote.QuoteNumber}
              </Typography>
            )}
            <Tooltip title={sideOpen ? 'Collapse' : 'Expand'} placement="right">
              <IconButton size="small" onClick={() => setSideOpen(!sideOpen)}>
                {sideOpen ? <ChevronLeft fontSize="small" /> : <ChevronRight fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>

          <List disablePadding sx={{ px: sideOpen ? 1.25 : 0.75, mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {SECTIONS.map((s) => {
              const selected = activeSection === s.key;
              const button = (
                <ListItemButton
                  selected={selected}
                  onClick={() => setActiveSection(s.key)}
                  sx={{
                    position: 'relative', borderRadius: 2, py: 1.1,
                    px: sideOpen ? 1.25 : 0, gap: sideOpen ? 1.25 : 0,
                    justifyContent: sideOpen ? 'flex-start' : 'center',
                    color: selected ? 'primary.main' : 'text.secondary',
                    '&.Mui-selected': { bgcolor: alpha(theme.palette.primary.main, 0.12) },
                    '&.Mui-selected:hover': { bgcolor: alpha(theme.palette.primary.main, 0.18) },
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                    '&::before': selected ? {
                      content: '""', position: 'absolute', left: 0, top: 7, bottom: 7,
                      width: 3, borderRadius: 4, bgcolor: theme.palette.primary.main,
                    } : undefined,
                  }}
                >
                  <s.Icon sx={{ fontSize: 20, color: selected ? 'primary.main' : 'text.disabled', flexShrink: 0 }} />
                  {sideOpen && (
                    <ListItemText
                      primary={s.label}
                      primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: selected ? 700 : 500, noWrap: true }}
                    />
                  )}
                </ListItemButton>
              );
              return sideOpen
                ? <Box key={s.key}>{button}</Box>
                : <Tooltip key={s.key} title={s.label} placement="right">{button}</Tooltip>;
            })}
          </List>
        </Box>

        {/* Center content */}
        <Box sx={{ flex: 1, p: 2.5, overflow: 'auto', minWidth: 0 }}>
          {/* FEAT-2026-0012 — persistent banner when a scenario is attached to this
              quote. Hidden once Converted: the plan and its tasks were re-keyed to
              the work order, so quote-side sync state is meaningless (and 'Sync
              now' is refused server-side, 409 QUOTE_CONVERTED). */}
          {quote.Status !== 'Converted' && (
            <ScheduleLinkBanner
              impact={scheduleImpact}
              loading={impactLoading}
              syncing={impactSyncing}
              onView={handleEditSchedule}
              onSync={handleSyncSchedule}
            />
          )}
          {/* freshReleaseCount is set the moment a status update returns scheduleRelease data.
              It is passed down so QuoteApprovalSection can show an immediate banner.
              When the quote is loaded fresh (page reload) with Declined/Withdrawn status,
              QuoteApprovalSection fetches the release summary independently. */}
          {/* Internal quotes skip the approval chrome — but the CONVERTED banner
              (name + Open Work Order link) must show for every quote type
              (2026-08-06: internal Q-2026-9104 had no path to its WO). */}
          {(!isInternal || quote.Status === 'Converted') && (
            <QuoteApprovalSection
              quote={quote}
              onRefresh={softRefresh}
              freshReleaseCount={freshReleaseCount}
              // G2-3: "Mark Expired" routes through handleUpdateQuote so the
              // committed-work 409 ceremony (Release & continue) applies.
              onStatusChange={(status) => { void handleUpdateQuote({ Status: status }); }}
            />
          )}
          {activeSection === 'header' && <QuoteHeaderSection quote={quote} onUpdate={handleUpdateQuote} readOnly={quote.Status !== 'Draft' && quote.Status !== 'Rejected' && quote.Status !== 'Changes Requested'} />}
          {activeSection === 'pricing' && <QuotePricingSection quote={quote} onUpdate={handleUpdateQuote} onRefresh={softRefresh} readOnly={quote.Status !== 'Draft' && quote.Status !== 'Rejected' && quote.Status !== 'Changes Requested'} />}
          {activeSection === 'scope' && <QuoteScopeSection quote={quote} onUpdate={handleUpdateQuote} readOnly={quote.Status !== 'Draft' && quote.Status !== 'Rejected' && quote.Status !== 'Changes Requested'} />}
          {activeSection === 'terms' && <QuoteTermsSection quote={quote} onUpdate={handleUpdateQuote} readOnly={quote.Status !== 'Draft' && quote.Status !== 'Rejected' && quote.Status !== 'Changes Requested'} />}
          {activeSection === 'activity' && <QuoteActivityLog quoteKey={quote.QuoteKey} />}
        </Box>

        {/* Right summary */}
        <QuoteSummary quote={quote} onDuplicate={() => setConfirmDlg({
          open: true,
          title: 'Duplicate Quote',
          message: `Create a copy of ${quote.QuoteNumber} with a new quote number? All line items will be copied.`,
          confirmLabel: 'Duplicate',
          confirmColor: 'primary',
          onConfirm: handleDuplicate,
        })} />
      </Box>

      <QuotePdfPreview open={pdfPreviewOpen} onClose={() => setPdfPreviewOpen(false)}
        quoteKey={quote.QuoteKey} quoteNumber={quote.QuoteNumber} />
      {/* Phase 5 Item 5 — read-only Gantt preview scoped to this quote */}
      <QuotePreviewScheduleModal
        open={previewScheduleOpen}
        onClose={() => setPreviewScheduleOpen(false)}
        quoteKey={quote.QuoteKey}
        quoteNumber={quote.QuoteNumber}
      />
      {/* Manual override (2026-07-24): admin-only status escape hatch. */}
      <OverrideStatusDialog
        open={overrideStatusOpen}
        quoteKey={quote.QuoteKey}
        currentStatus={quote.Status}
        onClose={() => setOverrideStatusOpen(false)}
        onOverridden={handleStatusOverridden}
      />
      <ConfirmDialog state={confirmDlg} onClose={() => setConfirmDlg(CONFIRM_INITIAL)} />
      <QuoteSendDialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)} quote={quote} onSent={softRefresh} />
      <Snackbar
        open={!!scheduleSnack}
        autoHideDuration={6000}
        onClose={() => setScheduleSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {scheduleSnack ? (
          <Alert severity={scheduleSnack.severity} variant="filled" onClose={() => setScheduleSnack(null)} sx={{ width: '100%' }}>
            {scheduleSnack.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default QuoteEditorPage;
